import React, { useCallback, useMemo, useState } from 'react';
import BottomPopup from '~/app/common/BottomPopup';
import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreActions, useStoreState } from '~/store';
import classes from './style.module.scss';
import Input from '~/app/modules/form/Search/Input';
import Checkbox from '~/app/modules/form/Checkbox';
import { WorkOrderHistoryPayloadType } from '../types';
import MultiSelect from '~/app/modules/form/MultiSelect';

export type SearchComponent = React.FC<{ machineView: boolean }>;

const Search: SearchComponent = ({ machineView }) => {
  const { t } = useTranslation('workorder.listing');
  const { t: ts } = useTranslation('workorder.statuses');
  const { t: tf } = useTranslation('workorder.filters');

  const departments = useStoreState(state => state.tasks.filterOptions.departments);
  const statuses = useStoreState(state => state.workOrder.filterOptions.status);
  const fetching = useStoreState(state => state.workOrder.fetching);
  const filters = useStoreState(state => machineView ? state.workOrder.machineFilters : state.workOrder.filters);
  const setFiltersAction = useStoreActions(actions => machineView ? actions.workOrder.setMachineFilters : actions.workOrder.setFilters);

  const [open, setOpen] = useState(false);
  const [departmentIds, setDepartmentIds] = useState<string[]>(filters.departments);
  const [statusIds, setStatusIds] = useState<string[]>(filters.status);
  const [onlyResponsible, setOnlyResponsible] = useState<boolean>(filters.type === WorkOrderHistoryPayloadType.MINE);

  const statusOptions = useMemo(() =>
    statuses && statuses.map(status => ({ text: ts(status.name), value: status.id.toString() })),
    [statuses, ts]
  );

  const departmentOptions = useMemo(() => 
    departments && departments.map(dept => ({ text: dept.number + ' - ' + dept.name, value: dept.id.toString() })),
    [departments]
  );

  const searchWorkorders = useCallback((searchTerm: string): void => {
    setFiltersAction({ searchText: searchTerm, type: filters.type, departments: departmentIds, status: statusIds });
  }, [setFiltersAction, filters.type, departmentIds, statusIds]);

  const checkboxClickHandler = useCallback((): void => {
    setOnlyResponsible(!onlyResponsible);
  }, [onlyResponsible, setOnlyResponsible]);

  const applyFilters = () => {
    setFiltersAction({ type: onlyResponsible ? WorkOrderHistoryPayloadType.MINE : WorkOrderHistoryPayloadType.ALL, searchText: filters.searchText.trim(), departments: departmentIds, status: statusIds });
    setOpen(false);
  }

  const clearAllFilters = () => {
    setOnlyResponsible(false);
    setFiltersAction({ type: WorkOrderHistoryPayloadType.ALL, searchText: filters.searchText, departments: [], status: [] });
    setOpen(false);
  };

  const handleMultiSelectChange = (key: string, selectedOptions: string[]) => {
    switch (key) {
      case 'department':
        setDepartmentIds(selectedOptions)
        break;
      case 'status':
        setStatusIds(selectedOptions)
        break;
      default:
        break;
    }
  }

  return (
    <div className={classes['search-wrap']}>
      <Input
        autoFocus={false}
        placeholder={t("search")}
        className={classes["field"]}
        inputClassName={classes["input"]}
        onSearch={searchWorkorders}
        fetching={fetching}
        value={filters.searchText}
        iconPosition='left'
      />
      <div className={`${classes['filter-button-wrapper']} ${(onlyResponsible || filters.departments?.length || filters.status?.length) ? classes['updated'] : ''}`} onClick={() => setOpen(true)}>
        <Icon
          name={'filter_list' as Icons}
          className={classes['bottomIcon']}
        />
      </div>
      <BottomPopup show={open} onClose={() => setOpen(false)}>
        <h3 className={classes['filter-popup-header']}>{tf('header')}</h3>
        <MultiSelect
          className={classes['input-container']}
          name="departmentId"
          placeholder={tf('department')}
          options={departmentOptions}
          defaultOptions={filters.departments}
          optionChange={(selectedOptions: string[]) => handleMultiSelectChange('department', selectedOptions)}
          icon="keyboard_arrow_down"
        />
        <MultiSelect
          className={classes['input-container']}
          name="status"
          placeholder={tf('status')}
          options={statusOptions}
          defaultOptions={filters.status}
          optionChange={(selectedOptions: string[]) => handleMultiSelectChange('status', selectedOptions)}
          icon="keyboard_arrow_down"
        />
        <Checkbox
          name='checkbox'
          className={classes['checkbox-wrapper']}
          inputClassName={`${classes['checkbox']} ${(onlyResponsible) ? classes['checked'] : ''}`}
          placeholder={tf('assignedToMe')}
          defaultChecked={onlyResponsible}
          checked={onlyResponsible}
          onChange={checkboxClickHandler}
        />
        <div className={classes['btn-container']}>
          <Button className={classes['clear-btn']} onClick={clearAllFilters}>
            {tf('clear')}
          </Button>
          <Button className={classes['apply-btn']} onClick={applyFilters}>
            {tf('apply')}
          </Button>
        </div>
      </BottomPopup>
    </div>
  );
};

export default Search